import React, { useCallback, useEffect, useRef, useState, } from 'react';
import isNil from 'lodash/isNil';
import { useDispatch } from 'react-redux';
import usePlacesAutocomplete, { getGeocode, getLatLng, } from 'use-places-autocomplete';
import { useTranslation } from '@yme/services/i18n';
import { EVENT_NAME_MAP, keyCodes, Status } from '@yme/utils/constants';
import { DropDownResults } from '../DropDownResults';
import { SingleInputForm } from '../SingleInputForm';
import { getSuggestionList, isStringAndNotEmpty } from '../helpers';
import { setGoogleSearchedPlace } from '../redux/actions';
import { ACCEPTED_KEYS, CACHE_DEFAULT_VALUE, CACHED_VALUE, DEBOUNCE_DEFAULT_VALUE, INITIAL_FIELD_STATE, MINIMAL_SYMBOL_COUNT, } from './constants';
import { FormStyled } from './styled';
export function GoogleSearchAutocomplete(props) {
    const { inputPlaceholder, currentCountry = 'NL', isRightToLeft, dispatchAction, titleLabel, isItemChosen, initialValue, value: incomingValue, } = props;
    const { language } = useTranslation();
    const [isExpandedDropDown, setIsExpandedDropDown] = useState(false);
    const [fieldState, setFieldState] = useState(INITIAL_FIELD_STATE);
    const [currIndex, setCurrIndex] = useState(null);
    const [searchLocation, setSearchLocation] = useState({
        lat: null,
        lng: null,
    });
    const [isDropdownValueSelected, setIsDropdownValueSelected] = useState(false);
    const [selectedValueByKeyboard, setSelectedValueByKeyboard] = useState(null);
    const args = {
        requestOptions: {
            componentRestrictions: {
                country: [`${currentCountry}`],
            },
            debounce: DEBOUNCE_DEFAULT_VALUE,
            cache: CACHE_DEFAULT_VALUE,
        },
        defaultValue: initialValue,
    };
    const minimalCountOfValue = fieldState.description.length >= MINIMAL_SYMBOL_COUNT;
    const { ready, value, suggestions: { status, data }, setValue, clearSuggestions, } = usePlacesAutocomplete(args);
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const clearedSuggestionList = getSuggestionList(data);
    const hasSuggestions = status === Status.OK;
    const hasDropDownExpanded = value.length >= MINIMAL_SYMBOL_COUNT &&
        clearedSuggestionList?.length > 0 &&
        !isDropdownValueSelected;
    const dismissSuggestions = useCallback(() => {
        setCurrIndex(null);
        clearSuggestions();
    }, [clearSuggestions]);
    const onClearHandler = () => {
        dismissSuggestions();
        setFieldState(INITIAL_FIELD_STATE);
    };
    // Get latitude and longitude via utility functions
    const getGeocodeHandler = (address) => {
        getGeocode({
            address,
        }).then((results) => {
            const { lat = null, lng = null } = getLatLng(results[0]);
            setFieldState({
                description: address,
                coordinates: {
                    lat,
                    lng,
                },
            });
            setSearchLocation({
                lat,
                lng,
            });
        });
    };
    const handleSelect = useCallback((props) => () => {
        const { description } = props;
        setValue(description, false);
        setIsDropdownValueSelected(true);
        dispatch(setGoogleSearchedPlace({
            latitude: null,
            longitude: null,
            value: description,
        }));
        dismissSuggestions();
        getGeocodeHandler(description);
    }, [dismissSuggestions, dispatch, setValue]);
    const handleEnter = (index) => () => {
        setCurrIndex(index);
    };
    const getNextIndexValue = ({ key }) => {
        if (key === keyCodes.ARROW_UP) {
            const currentValue = currIndex ?? clearedSuggestionList.length;
            const nextIndex = currentValue && currentValue > 0 ? currentValue - 1 : null;
            if (nextIndex !== null) {
                setSelectedValueByKeyboard(nextIndex);
            }
            return nextIndex;
        }
        if (key === keyCodes.ARROW_DOWN) {
            const currentValue = currIndex ?? -1;
            const nextIndex = currentValue < clearedSuggestionList.length - 1 ? currentValue + 1 : null;
            if (nextIndex !== null) {
                setSelectedValueByKeyboard(nextIndex);
            }
            return nextIndex;
        }
        setSelectedValueByKeyboard(null);
        return null;
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            dismissSuggestions();
        }
        if (!hasSuggestions || (event?.key && !ACCEPTED_KEYS.includes(event?.key))) {
            return;
        }
        const nextIndex = getNextIndexValue(event);
        if (event.key === keyCodes.ENTER) {
            getGeocodeHandler(event.currentTarget.value);
            dismissSuggestions();
        }
        if (typeof nextIndex === 'number') {
            setCurrIndex(nextIndex);
            if (!isNil(nextIndex)) {
                setValue(clearedSuggestionList[nextIndex]
                    ? clearedSuggestionList[nextIndex].description
                    : CACHED_VALUE, false);
            }
        }
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        setIsExpandedDropDown(false);
    };
    const onFocusHandler = useCallback(() => {
        if (minimalCountOfValue) {
            setValue(fieldState.description, true);
            setIsExpandedDropDown(true);
        }
        if (value.length >= MINIMAL_SYMBOL_COUNT) {
            setValue(value, true);
            setIsExpandedDropDown(true);
        }
    }, [fieldState.description, minimalCountOfValue, setValue, value]);
    useEffect(() => {
        if (searchLocation?.lat && searchLocation?.lng) {
            dispatchAction({
                coordinates: searchLocation,
                country: currentCountry,
                language,
                dispatch,
            });
        }
    }, [
        currentCountry,
        dispatch,
        dispatchAction,
        language,
        searchLocation,
        searchLocation?.lat,
        searchLocation?.lng,
    ]);
    useEffect(() => {
        if (!value.length) {
            setIsExpandedDropDown(false);
        }
        if (value.length >= 4 || minimalCountOfValue) {
            setIsExpandedDropDown(true);
        }
    }, [value, fieldState, minimalCountOfValue]);
    useEffect(() => {
        const handle = (event) => {
            if (!formRef?.current?.contains(event.target)) {
                setIsExpandedDropDown(false);
                dismissSuggestions();
            }
        };
        document.addEventListener(EVENT_NAME_MAP.click, handle);
        return () => {
            document.removeEventListener(EVENT_NAME_MAP.click, handle);
        };
    }, [setIsExpandedDropDown, formRef, dismissSuggestions]);
    useEffect(() => {
        if (isStringAndNotEmpty(incomingValue)) {
            setValue(incomingValue, false);
        }
    }, [incomingValue, setValue]);
    useEffect(() => {
        if (isStringAndNotEmpty(initialValue)) {
            setValue(initialValue, true);
        }
    }, [initialValue, setValue]);
    const setValueHandler = useCallback((opt) => {
        setIsDropdownValueSelected((prev) => prev && false);
        setValue(opt);
    }, [setValue]);
    return (React.createElement(FormStyled, { onSubmit: handleSubmit, ref: formRef },
        React.createElement(SingleInputForm, { placeholder: inputPlaceholder, isRightToLeft: isRightToLeft, setIsExpandedDropDown: setIsExpandedDropDown, fieldState: {
                description: value,
                coordinates: {
                    lat: null,
                    lng: null,
                },
            }, setFieldState: setValueHandler, handleKeyDown: handleKeyDown, ready: ready, onFocusHandler: onFocusHandler, isItemChosen: isItemChosen, onClear: onClearHandler, titleLabel: titleLabel }),
        hasDropDownExpanded && (React.createElement(DropDownResults, { isRightToLeft: isRightToLeft, activeItem: value, isExpandedDropDown: isExpandedDropDown, suggestionList: clearedSuggestionList, currIndex: currIndex, searchedValue: fieldState, handleSelect: handleSelect, selectedValueByKeyboard: selectedValueByKeyboard, handleEnter: handleEnter }))));
}
